// apps/client/src/hooks/useTrailRunOffPortfolio.ts
// Always-on portfolio metrics for the Broker Trail Run-Off page stat cards.
// Fires TWO parallel getTrailRunOffReport calls — current 12mo + previous
// 12mo — so each card can show a period-over-period delta chip.
//
// Why two calls instead of one bigger query: callable signature already
// supports a single window; adding a server-side comparison would require
// a backend deploy. Two parallel callable invocations are launched via
// Promise.all so total latency is max(call_a, call_b), not their sum.

import { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface PortfolioChanges {
  /** % change in total loan book on trail. null if previous value is null/zero. */
  totalLineOfTrailPct: number | null;
  /** % change in active trail balance. */
  activeTrailBalancePct: number | null;
  /** % change in inactive trail balance. */
  inactiveTrailBalancePct: number | null;
  /** % change in count of distinct loans paying trail. */
  totalLoansWithTrailPct: number | null;
  /** % change in trail commission revenue. */
  totalTrailCommissionPct: number | null;
  /** Percentage-POINT delta in run-off rate (since the metric itself is a %). */
  runOffPercentagePctPoints: number | null;
}

export interface UseTrailRunOffPortfolioReturn {
  totalLineOfTrailCents: number | null;
  activeTrailBalanceCents: number | null;
  inactiveTrailBalanceCents: number | null;
  totalLoansWithTrail: number | null;
  /** Trail commission $ paid across all loans in the rolling 12-month window. */
  totalTrailCommissionCents: number | null;
  /** Run-off rate (%) for the current window. */
  runOffPercentage: number | null;
  /** Period-over-period deltas (current vs previous 12 months). */
  changes: PortfolioChanges;
  /** Human-readable period label for the CURRENT window, e.g. "May 2025 – Apr 2026". */
  periodLabel: string;
  loading: boolean;
  error: string | null;
}

interface RawReport {
  activeTrailBalanceCents?: number;
  inactiveTrailBalanceCents?: number;
  totalLineOfTrailCents?: number;
  runOffPercentage?: number;
  summary?: {
    totalLoansWithTrail?: number;
    totalTrailCommissionCents?: number;
  };
}

function pctChange(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function ppDelta(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current == null || previous == null) return null;
  return current - previous;
}

/**
 * Rolling 12-month portfolio view + previous-12-month comparison.
 */
export function useTrailRunOffPortfolio({
  companyId,
  officeId,
}: {
  companyId: string;
  officeId?: string;
}): UseTrailRunOffPortfolioReturn {
  const [current, setCurrent] = useState<RawReport | null>(null);
  const [previous, setPrevious] = useState<RawReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { currentWindow, previousWindow, periodLabel } = useMemo(() => {
    const now = new Date();
    // Current: last completed month back 12.
    const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1);
    // Previous: same window shifted -12 months.
    const prevEndDate = new Date(startDate.getFullYear(), startDate.getMonth(), 0);
    const prevStartDate = new Date(prevEndDate.getFullYear(), prevEndDate.getMonth() - 11, 1);

    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const fmtStart = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const label = `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()} – ${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;

    return {
      currentWindow: { startMonth: fmtStart(startDate), endMonth: fmt(endDate) },
      previousWindow: { startMonth: fmtStart(prevStartDate), endMonth: fmt(prevEndDate) },
      periodLabel: label,
    };
  }, []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchPortfolio = async () => {
      setLoading(true);
      setError(null);

      const getTrailRunOffReport = httpsCallable(functions, 'getTrailRunOffReport');
      const callOnce = (window: { startMonth: string; endMonth: string }) =>
        getTrailRunOffReport({
          companyId,
          officeId,
          year: new Date().getFullYear() - 1,
          yearType: 'calendar',
          startMonth: window.startMonth,
          endMonth: window.endMonth,
        }).then((res) => res.data as RawReport);

      try {
        // Fire both windows in parallel. If the previous window fails (e.g. no
        // historical data), still surface the current window — deltas just
        // render as null/no-chip.
        const [currResult, prevResult] = await Promise.allSettled([
          callOnce(currentWindow),
          callOnce(previousWindow),
        ]);

        if (currResult.status === 'fulfilled') {
          setCurrent(currResult.value);
        } else {
          throw currResult.reason;
        }
        if (prevResult.status === 'fulfilled') {
          setPrevious(prevResult.value);
        } else {
          // Non-fatal — log and continue without deltas.
          console.warn('[useTrailRunOffPortfolio] previous-window fetch failed; deltas hidden:', prevResult.reason);
          setPrevious(null);
        }
      } catch (err: unknown) {
        console.error('[useTrailRunOffPortfolio] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch portfolio data');
        setCurrent(null);
        setPrevious(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [companyId, officeId, currentWindow, previousWindow]);

  const changes: PortfolioChanges = useMemo(() => ({
    totalLineOfTrailPct: pctChange(current?.totalLineOfTrailCents, previous?.totalLineOfTrailCents),
    activeTrailBalancePct: pctChange(current?.activeTrailBalanceCents, previous?.activeTrailBalanceCents),
    inactiveTrailBalancePct: pctChange(current?.inactiveTrailBalanceCents, previous?.inactiveTrailBalanceCents),
    totalLoansWithTrailPct: pctChange(current?.summary?.totalLoansWithTrail, previous?.summary?.totalLoansWithTrail),
    totalTrailCommissionPct: pctChange(current?.summary?.totalTrailCommissionCents, previous?.summary?.totalTrailCommissionCents),
    runOffPercentagePctPoints: ppDelta(current?.runOffPercentage, previous?.runOffPercentage),
  }), [current, previous]);

  return {
    totalLineOfTrailCents: current?.totalLineOfTrailCents ?? null,
    activeTrailBalanceCents: current?.activeTrailBalanceCents ?? null,
    inactiveTrailBalanceCents: current?.inactiveTrailBalanceCents ?? null,
    totalLoansWithTrail: current?.summary?.totalLoansWithTrail ?? null,
    totalTrailCommissionCents: current?.summary?.totalTrailCommissionCents ?? null,
    runOffPercentage: current?.runOffPercentage ?? null,
    changes,
    periodLabel,
    loading,
    error,
  };
}
